import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../AuthContext';
import configData from '../../configure';
import { getFacultyAssignedClassNames, filterClassesForFaculty } from '../../utils/facultyHierarchyUtils';
import { toast } from 'react-toastify';
import {
  fetchAttendanceRecords,
  createAttendanceRecord
} from '../../services/attendanceService';
import './CenterVisits.css';

// Material Icons
import FmdGoodIcon from '@mui/icons-material/FmdGood';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import Select from 'react-select';

const getDistanceInMeters = (lat1, lon1, lat2, lon2) => {
  const R = 6371e3; // metres
  const phi1 = lat1 * Math.PI / 180;
  const phi2 = lat2 * Math.PI / 180;
  const deltaPhi = (lat2 - lat1) * Math.PI / 180;
  const deltaLambda = (lon2 - lon1) * Math.PI / 180;

  const a = Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
            Math.cos(phi1) * Math.cos(phi2) *
            Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

const CenterVisits = ({ orgId }) => {
  const { axios, user, facultyInfo, userRole } = useAuth();
  
  // Data States
  const [nirvahaks, setNirvahaks] = useState([]);
  const [classesList, setClassesList] = useState([]);
  const [historyLogs, setHistoryLogs] = useState([]);
  const [allSupervisorsList, setAllSupervisorsList] = useState([]);
  const [currentLoc, setCurrentLoc] = useState(null);
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [logFilter, setLogFilter] = useState('THIS_MONTH');
  const [facultyFilter, setFacultyFilter] = useState('ALL_FACULTY');

  // Get Browser Location coordinates
  const getGPSLocation = () => {
    return new Promise((resolve) => {
      setGpsLoading(true);
      if (!navigator.geolocation) {
        toast.error("Geolocation is not supported by your browser.");
        setGpsLoading(false);
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          };
          setCurrentLoc(coords);
          setGpsLoading(false);
          resolve(coords);
        },
        (error) => {
          console.error("GPS Error:", error);
          toast.warning("Could not lock GPS coordinates. Make sure location permissions are enabled.");
          setGpsLoading(false);
          resolve(null);
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 }
      );
    });
  };

  // Load all necessary data
  const loadData = async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    try {
      const activeOrg = orgId || user?.orgId;
      if (!activeOrg) return;

      // 1. Lock GPS location
      const coords = await getGPSLocation();

      // 2. Fetch all faculty members (using page iteration loop)
      let lastEvaluatedKey = null;
      let allFaculties = [];
      const seenKeys = new Set();
      let isFirstPage = true;
      do {
        const res = await axios.get(`${configData.GET_USERS_BY_TYPE}/${activeOrg}`, {
          params: {
            userType: 'FACULTY',
            pageSize: isFirstPage ? 50 : 1000,
            lastEvaluatedKey: lastEvaluatedKey || undefined
          },
          headers: { accept: 'application/hal+json' },
        });
        const rawData = res.data;
        const pageItems = Array.isArray(rawData)
          ? rawData
          : Array.isArray(rawData?.items)
          ? rawData.items
          : Array.isArray(rawData?.content)
          ? rawData.content
          : [];
        allFaculties = allFaculties.concat(pageItems);

        lastEvaluatedKey = rawData?.lastEvaluatedKey;
        if (lastEvaluatedKey && seenKeys.has(lastEvaluatedKey)) break;
        if (lastEvaluatedKey) seenKeys.add(lastEvaluatedKey);
        isFirstPage = false;
      } while (lastEvaluatedKey);

      // 3. Determine logged-in user's role in the KV hierarchy
      const loggedInUserId = user?.userId;
      const loggedInFacultyType = facultyInfo?.facultyType;
      const isAdmin = user?.role === 'ADMIN' || user?.userType === 'ADMIN';

      // Store all supervisors for the filter dropdown
      const allSupervisors = allFaculties
        .filter((f) => ['NIRVAHAK', 'PARYAVEKSHAK', 'SAMANVAYKARTA'].includes(f?.facultyInfo?.facultyType))
        .map((f) => ({
          userId: f?.user?.userId || f?.userId,
          name: [f?.user?.firstName, f?.user?.middleName, f?.user?.lastName].filter(Boolean).join(' ') || f?.name || 'Unknown'
        }));
      setAllSupervisorsList(allSupervisors);

      // Collect all Nirvahaks from allFaculties
      const allNirvahaks = allFaculties
        .filter((f) => f?.facultyInfo?.facultyType === 'NIRVAHAK')
        .map((f) => {
          const fInfo = f?.facultyInfo || {};
          const uAttr = f?.user?.additionalAttributes || f?.additionalAttributes || {};
          const lat = parseFloat(uAttr.latitude || fInfo.latitude);
          const lon = parseFloat(uAttr.longitude || fInfo.longitude);
          return {
            userId: f?.user?.userId || f?.userId,
            name: [f?.user?.firstName, f?.user?.middleName, f?.user?.lastName].filter(Boolean).join(' ') || f?.name || 'Unknown',
            employeeId: fInfo.employeeId || '',
            centerName: uAttr.assignedClasses || fInfo.responsibilities?.[0] || 'Unassigned Center',
            area: uAttr.area || 'N/A',
            latitude: isNaN(lat) ? null : lat,
            longitude: isNaN(lon) ? null : lon,
            supervisorId: fInfo.supervisorId || null,
          };
        });

      let filteredNirvahaks;

      if (isAdmin) {
        // Admin sees all Nirvahaks
        filteredNirvahaks = allNirvahaks;
      } else if (loggedInFacultyType === 'PARYAVEKSHAK') {
        // Paryavekshak sees only their own Nirvahaks (where supervisorId === my userId)
        filteredNirvahaks = allNirvahaks.filter(
          (n) => n.supervisorId === loggedInUserId
        );
      } else if (loggedInFacultyType === 'SAMANVAYKARTA') {
        // Samanvaykartha sees Nirvahaks whose Paryavekshak's supervisorId is their userId
        // Step 1: Find all Paryavekshaks under this Samanvaykartha
        const myParyavekshakIds = allFaculties
          .filter((f) =>
            f?.facultyInfo?.facultyType === 'PARYAVEKSHAK' &&
            f?.facultyInfo?.supervisorId === loggedInUserId
          )
          .map((f) => f?.user?.userId || f?.userId);

        // Step 2: Filter Nirvahaks whose supervisorId is in that Paryavekshak list
        filteredNirvahaks = allNirvahaks.filter(
          (n) => myParyavekshakIds.includes(n.supervisorId)
        );
      } else {
        // Default: show nothing if role is not recognised
        filteredNirvahaks = [];
      }

      setNirvahaks(filteredNirvahaks);

      // 3. Fetch Classes
      const classRes = await axios.get(`${configData.GET_CLASSES_BY_ORG}/${activeOrg}`);
      if (classRes.data) {
        let classes = Array.isArray(classRes.data) ? classRes.data : [];
        const assignedClassNames = await getFacultyAssignedClassNames(activeOrg, user, axios);
        classes = filterClassesForFaculty(classes, assignedClassNames);
        setClassesList(classes);
      }

      // 4. Fetch Visit History
      let allVisits = [];

      if (isAdmin) {
        // Admin sees visits for all faculty members who could check in
        const relevantUsers = allFaculties.filter(f => ['NIRVAHAK', 'PARYAVEKSHAK', 'SAMANVAYKARTA'].includes(f?.facultyInfo?.facultyType));
        const promises = relevantUsers.map(f => {
           const id = f?.user?.userId || f?.userId;
           if (!id) return Promise.resolve([]);
           return fetchAttendanceRecords({ filterType: 'user', id, pageSize: 100 })
             .then(res => res?.items || res || [])
             .catch(() => []);
        });
        const results = await Promise.all(promises);
        allVisits = results.flat();
      } else {
        const currentUserId = user?.userId;
        if (currentUserId) {
          try {
            const historyRes = await fetchAttendanceRecords({
              filterType: 'user',
              id: currentUserId,
              pageSize: 100
            });
            allVisits = historyRes?.items || historyRes || [];
          } catch (err) {
            console.error("Error fetching history:", err);
          }
        }
      }

      const filteredVisits = allVisits
        .filter((a) => a.subject === 'Center Visit')
        .sort((a, b) => new Date(b.checkInTime).getTime() - new Date(a.checkInTime).getTime());
      
      setHistoryLogs(filteredVisits);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load center details or history logs.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [orgId]);

  // Handle Check-In click
  const handleCheckInClick = async (center) => {
    let coords = await getGPSLocation();
    
    if (!coords) {
      if (currentLoc) {
        coords = currentLoc; // fallback to last known location if re-lock fails
      } else {
        toast.error("GPS Location could not be found. Check-in is not allowed.");
        return;
      }
    }

    if (center.latitude === null || center.longitude === null) {
      toast.error("Center coordinates are missing. Check-in is disabled.");
      return;
    }

    const dist = getDistanceInMeters(coords.latitude, coords.longitude, center.latitude, center.longitude);
    const resolvedName = resolveCenterName(center.centerName);
    if (dist <= 50) {
      if (window.confirm(`You are within range (${Math.round(dist)}m) of ${resolvedName}. Confirm check-in visit?`)) {
        submitCheckIn(center, 'MOBILE_APP_CHECK_IN', `Location verified: ${Math.round(dist)}m away.`, resolvedName);
      }
    } else {
      toast.error(`You are out of range (${Math.round(dist)}m away). Check-in requires you to be within 50m of the center.`);
    }
  };

  const submitCheckIn = async (center, method, remarks, resolvedName) => {
    setSubmitting(true);
    try {
      const activeOrg = orgId || user?.orgId;
      
      const ids = (center.centerName || '').split(',').map(s => s.trim());
      const classId = ids[0] || 'center-visit-fallback';

      const payload = {
        orgId: activeOrg,
        classId: classId,
        userId: user?.userId,
        academicYear: '2026-2027',
        attendanceDate: new Date().toISOString().split('T')[0],
        checkInTime: new Date().toISOString(),
        userType: 'FACULTY',
        attendanceStatus: 'PRESENT',
        classOrLocation: resolvedName || resolveCenterName(center.centerName),
        subject: 'Center Visit',
        remarks: remarks,
        attendanceMethod: method,
        createdBy: user?.name || 'admin',
        lastUpdatedBy: user?.name || 'admin'
      };

      const res = await createAttendanceRecord(payload);
      if (res) {
        toast.success(`Check-in logged successfully at ${resolvedName || resolveCenterName(center.centerName)}!`);
        loadData(false);
      } else {
        toast.error("Check-in submission failed.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Check-in error occurred.");
    } finally {
      setSubmitting(false);
    }
  };

  const resolveCenterName = (centerNameStr) => {
    if (!centerNameStr || centerNameStr === 'Unassigned Center') return centerNameStr;
    const ids = centerNameStr.split(',').map(s => s.trim());
    const names = ids.map(id => {
      const cls = classesList.find(c => String(c.classId) === id || String(c.id) === id);
      return cls ? `${cls.className || ''} ${cls.sectionName || ''}`.trim() : id;
    });
    return names.join(', ');
  };

  // Filter list of centers based on search query
  const filteredCenters = nirvahaks.filter(c => {
    const resolvedName = resolveCenterName(c.centerName).toLowerCase();
    const query = searchQuery.toLowerCase();
    return resolvedName.includes(query) ||
           c.name.toLowerCase().includes(query) ||
           c.area.toLowerCase().includes(query);
  });

  const uniqueSupervisors = Array.from(new Set([
    ...historyLogs.map(log => log.createdBy),
    ...allSupervisorsList.map(s => s.name)
  ])).filter(Boolean).sort();
  
  const filteredLogs = historyLogs.filter(log => {
    let matchesDate = true;
    if (logFilter === 'THIS_MONTH') {
      const logDate = new Date(log.attendanceDate);
      const now = new Date();
      matchesDate = logDate.getMonth() === now.getMonth() && logDate.getFullYear() === now.getFullYear();
    }
    let matchesFaculty = true;
    if (facultyFilter !== 'ALL_FACULTY') {
      matchesFaculty = log.createdBy === facultyFilter;
    }
    return matchesDate && matchesFaculty;
  });

  return (
    <div className="center-visits-container">
      {/* Header Banner */}
      <div className="visits-header-card">
        <div className="header-info">
          <h1>Center Visits</h1>
        </div>
        <div className="gps-indicator-badge">
          <GpsFixedIcon className={`gps-badge-icon ${currentLoc ? 'active' : ''}`} />
          <div className="gps-details">
            <span className="gps-label">Location Status</span>
            <span className="gps-coords-value">
              {currentLoc ? 'GPS Active' : 'Locating...'}
            </span>
          </div>
          <button className="relock-location-btn" onClick={getGPSLocation} disabled={gpsLoading}>
            {gpsLoading ? <span className="spinner-mini" /> : <RefreshIcon />}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="visits-loading-wrap">
          <span className="loading-spinner" />
          <p>Loading centers...</p>
        </div>
      ) : (
        <div className="visits-layout-grid">
          {/* Left Panel: Active Centers List */}
          <div className="centers-list-panel">
            <div className="panel-header">
              <h2>Active Centers</h2>
              <div className="search-bar-wrap">
                <SearchIcon />
                <input
                  type="text"
                  placeholder="Search centers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            <div className="centers-scroll-area">
              {filteredCenters.length === 0 ? (
                <div className="no-centers-card">
                  <InfoOutlinedIcon />
                  <p>No matching active KV centers found.</p>
                </div>
              ) : (
                filteredCenters.map((center) => {
                  let distanceText = 'N/A';
                  let inRange = false;
                  let hasLoc = center.latitude !== null && center.longitude !== null;

                  if (hasLoc && currentLoc) {
                    const dist = getDistanceInMeters(currentLoc.latitude, currentLoc.longitude, center.latitude, center.longitude);
                    inRange = dist <= 50;
                    distanceText = dist < 1000 ? `${Math.round(dist)}m` : `${(dist / 1000).toFixed(1)}km`;
                  }

                  return (
                    <div key={center.userId} className="center-visit-item-card">
                      <div className="item-card-header">
                        <div className="center-meta">
                          <h3>{resolveCenterName(center.centerName)}</h3>
                          <span className="nirvahak-label">Nirvahak: <strong>{center.name}</strong></span>
                          <span className="area-label"><FmdGoodIcon /> {center.area}</span>
                        </div>
                        <div className="range-badge-col">
                          {hasLoc ? (
                            <span className={`distance-pill ${inRange ? 'in-range' : 'out-of-range'}`}>
                              {distanceText}
                            </span>
                          ) : (
                            <span className="distance-pill no-gps">No GPS</span>
                          )}
                        </div>
                      </div>

                      <button
                        className={`item-checkin-btn ${inRange ? 'verified' : 'bypass'}`}
                        onClick={() => handleCheckInClick(center)}
                        disabled={submitting}
                      >
                        <FmdGoodIcon /> Check In
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Right Panel: Visit Logs Table */}
          <div className="visit-history-panel">
            <div className="panel-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2>Supervisor Visit Log</h2>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {userRole === 'ADMIN' && (
                  <Select
                    className="log-filter-select"
                    classNamePrefix="react-select"
                    value={{ value: facultyFilter, label: facultyFilter === 'ALL_FACULTY' ? 'All Supervisors' : facultyFilter }}
                    onChange={(selected) => setFacultyFilter(selected.value)}
                    options={[
                      { value: 'ALL_FACULTY', label: 'All Supervisors' },
                      ...uniqueSupervisors.map(name => ({ value: name, label: name }))
                    ]}
                    styles={{
                      container: (provided) => ({ ...provided, minWidth: '220px' }),
                      control: (provided) => ({ ...provided, minHeight: '38px', borderRadius: '4px' })
                    }}
                  />
                )}
                <select 
                  className="log-filter-select"
                  value={logFilter}
                  onChange={(e) => setLogFilter(e.target.value)}
                  style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc', height: '38px' }}
                >
                  <option value="THIS_MONTH">This Month</option>
                  <option value="ALL_TIME">All Time</option>
                </select>
              </div>
            </div>
            
            <div className="history-table-container">
              {filteredLogs.length === 0 ? (
                <div className="no-history-card">
                  <InfoOutlinedIcon />
                  <p>No visit attendance checks recorded yet.</p>
                </div>
              ) : (
                <table className="visits-log-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Center Location</th>
                      {userRole === 'ADMIN' && <th>Supervisor</th>}
                      <th>Status</th>
                      <th>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLogs.map((log) => {
                      const formattedDate = new Date(log.attendanceDate).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      });
                      const formattedTime = log.checkInTime ? new Date(log.checkInTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'N/A';
                      const isBypassed = log.attendanceMethod === 'LOCATION_TRACKING' || log.remarks?.toLowerCase().includes('bypass');

                      return (
                        <tr key={log.attendanceId}>
                          <td>
                            <div style={{ fontWeight: 600 }}>{formattedDate}</div>
                            <div style={{ fontSize: '11px', color: '#64748B' }}>{formattedTime}</div>
                          </td>
                          <td className="center-cell">{resolveCenterName(log.classOrLocation)}</td>
                          {userRole === 'ADMIN' && <td style={{ fontWeight: 500 }}>{log.createdBy}</td>}
                          <td>
                            <span className={`status-pill ${isBypassed ? 'bypassed' : 'verified'}`}>
                              {isBypassed ? 'Bypassed' : 'Verified'}
                            </span>
                          </td>
                          <td className="remarks-cell">{log.remarks}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CenterVisits;
